import { Injectable } from '@angular/core';
import { StoreData } from '../models/store-data';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  skipWhile,
  switchMap,
  tap,
} from 'rxjs';
import { CmsService } from './cms.service';
import { ChannelService } from './channel.service';
import { StrapiResponse } from '../../_shared/models/cms/strapi-response';
import { CmsReliance } from '../../_shared/models/cms/cms-reliance';
import { StrapiFactory } from '../../_shared/models/cms/strapi-factory';
import { SfSelectOption } from 'ngx-smart-form';
import { ProductType } from 'src/app/requests/models/product';
import { ChannelType } from '../../settings/canals/channel-type';
import { CmsProduct } from '../../_shared/models/cms/cms-product';
import { FlatRelianceProduct } from '../../_shared/models/cms/flat/flat-reliance-product';
import { Channel } from '../../settings/canals/channel';
import { PraticaType } from '../../../interfaces/pratica';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class LoanService {
  private _loanProductsData = new BehaviorSubject(
    new StoreData<FlatRelianceProduct[]>()
  );

  get loanProducts$() {
    if (
      this._loanProductsData.value.state !== 'completed' &&
      this.auth.me?.canaleId
    ) {
      return this.getChannelProducts(this.auth.me.canaleId);
    } else {
      return this._loanProductsData.asObservable();
    }
  }

  get loanProductsTypes() {
    return this.loanProducts$.pipe(
      filter((loanProductsData) => loanProductsData.state === 'completed'),
      map((loanProductsData) =>
        (loanProductsData.data || []).map((product) => product.type)
      )
    );
  }

  /*  get isAml() {
    if (!this.auth.me?.canaleId) return of(false);
    return this.channelType$.pipe(
      map((channelType) => channelType === ChannelType.CMC)
    );
  } */

  private _channel = new StoreData<Channel>();
  get channel$() {
    if (this._channel.state !== 'completed') {
      return this.channelService.getByID(this.auth.me!.canaleId).pipe(
        tap((channel) => {
          this._channel.update('completed', channel);
        }),
        catchError((err) => {
          this._channel.update('error');
          return of(undefined);
        })
      );
    } else {
      return of(this._channel.data);
    }
  }

  /*   get channelType$() {
    if (this._channel.state === 'init' || this._channel.state === 'error') {
      return this.getChannelType(this.auth.me!.canaleId);
    } else {
      return of(this._channel.data?.type);
    }
  } */

  /*   private getChannelType(channelId: string) {
    let channel$: Observable<Channel | undefined>;

    this._channel.update('waiting');
    channel$ = this.channelService.getByID(channelId).pipe(
      tap((channel) => {
        this._channel.update('completed', channel);
      }),
      catchError((err) => {
        this._channel.update('error');
        return of(undefined);
      })
    );

    return channel$.pipe(map((channel) => channel?.type));
  } */

  constructor(
    private channelService: ChannelService,
    private cmsService: CmsService,
    private auth: AuthService
  ) {}

  private getChannelProducts(
    channelId: string
  ): Observable<StoreData<FlatRelianceProduct[]>> {
    this._loanProductsData.next(this._loanProductsData.value.update('waiting'));
    let request$ = forkJoin([
      this.channelService.getByID(channelId),
      this.cmsService.getAffidamento(),
    ]);

    return request$.pipe(
      map(([channel, affidamento]) => {
        this._channel.update('completed', channel);
        return this.parseReliance(
          affidamento[0],
          channel.prodotti,
          channel.type
        );
      }),
      tap((loanProducts) =>
        this._loanProductsData.next(
          this._loanProductsData.value.update('completed', loanProducts)
        )
      ),
      catchError((err) => {
        this._channel.update('error');
        this._loanProductsData.next(
          this._loanProductsData.value.update('error', [])
        );
        return of([]);
      }),
      switchMap(() => this._loanProductsData.asObservable())
    );
  }

  parseReliance(
    strapiResponse: StrapiResponse<CmsReliance, 'reliance'>,
    channelProducts: ProductType[],
    channelType?: ChannelType
  ): FlatRelianceProduct[] {
    let reliance = StrapiFactory.responseToView(strapiResponse, 'reliance');

    let flatProducts: FlatRelianceProduct[] = reliance.products
      .map((product) => LoanService.mapProduct(product))
      .filter((relianceProduct) => {
        if (channelType === 'CDC' || channelType === 'SGR') return true;
        return channelProducts.includes(relianceProduct.type);
      });

    return flatProducts;
  }

  getOptions({
    channelId,
    praticaType,
  }: {
    channelId: string;
    praticaType: PraticaType;
  }): Observable<SfSelectOption[]> {
    return this.loanProducts$.pipe(
      filter(
        (loanProductsData) =>
          loanProductsData.state === 'completed' ||
          loanProductsData.state === 'error'
      ),
      map((loanProductsData) => {
        return loanProductsData
          .data!.filter((prod) => prod.pratica === praticaType)
          .map((product) => ({
            view: product.name,
            value: product.type,
          }));
      })
    );
  }

  static mapProduct(product: CmsProduct): FlatRelianceProduct {
    let flatProduct: FlatRelianceProduct;
    flatProduct = StrapiFactory.flatStuff(product, [
      'documents',
      ['document'],
    ]) as unknown as FlatRelianceProduct;
    flatProduct = StrapiFactory.flatStuff(product, [
      'target',
      ['positive', 'negative'],
    ]) as unknown as FlatRelianceProduct;

    let rP = flatProduct as unknown as FlatRelianceProduct;

    return rP;
  }
}
